/**
 * CORE-642 — choose between an avatar's original and its small preview.
 *
 * Avatars are stored as whatever the user uploaded (measured on prod: 14 master
 * photos = 7.0 MB, the largest a 2268×4032 phone shot at 2.5 MB), while chat
 * lists draw them at 36–48 px. The backend now stores a 192 px webp preview next
 * to the original and returns it in a sibling field (`avatarThumbUrl` /
 * `photoThumbUrl`); this decides which of the two a given surface should ask for.
 *
 * The rule is by RENDERED SIZE, not by screen: a preview used above its natural
 * size looks soft, and the profile header — which is what the original is kept
 * for — must keep getting the full-resolution file.
 */
import { PixelRatio } from 'react-native';

/** Longest edge of the generated preview. Mirrors AvatarThumbnailKey.Size on the backend. */
export const AVATAR_PREVIEW_SIZE = 192;

/**
 * The preview when it is big enough for `renderedSize`, else the original.
 *
 * `renderedSize` is in layout points; it is multiplied by the device pixel ratio
 * because that is what the image actually has to fill. So a 48 pt avatar asks for
 * 96 px on a 2x screen (preview) and 144 px on a 3x one (still the preview),
 * while an 80 pt one needs 240 px on 3x and correctly falls back to the original.
 *
 * Returns null only when there is no image at all, so callers keep their existing
 * "no avatar → initials/placeholder" branch untouched.
 */
export function pickAvatarUrl(
  original: string | null | undefined,
  thumb: string | null | undefined,
  renderedSize: number,
): string | null {
  if (!thumb) return original ?? null;
  if (!original) return thumb;

  // Guard against a caller passing 0 / NaN (an un-laid-out element): without a
  // real size we cannot say the preview is enough, so keep today's behaviour.
  if (!Number.isFinite(renderedSize) || renderedSize <= 0) return original;

  const needed = renderedSize * PixelRatio.get();
  return needed <= AVATAR_PREVIEW_SIZE ? thumb : original;
}
